import type { CAButtonStyle, CAButtonType } from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

import CAButton from './CAButton'

/**
 * The `CAButtonGroupFooter` component is used to display the footer of a button group.
 * @component CAButtonGroupFooter
 *
 * @prop {string} [backDataTest='modal-back-button'] - The data-test attribute for the back button.
 * @prop {string} [backText='Back'] - The text for the back button.
 * @prop {CAButtonType} [buttonType='primary'] - The type of together the button.
 * @prop {String} [buttonsDirection='row'] - The direction of the buttons in the footer: 'row' | 'column'
 * @prop {string} [cancelDataTest='modal-close-button'] - The data-test attribute for the cancel button.
 * @prop {string} [cancelText='Cancel'] - The text for the cancel button.
 * @prop {boolean} [disabled=false] - Whether the buttons are disabled.
 * @prop {boolean} [disabledCancel=false] - Whether the cancel button is disabled.
 * @prop {string} [nextDataTest='modal-next-button'] - The backDataTest attribute for the next button.
 * @prop {string} [nextText='Next'] - The text for the next button.
 * @prop {boolean} [showBackButton=false] - Whether to show the back button.
 * @prop {boolean} [showCancelButton=true] - Whether to show the cancel button.
 * @prop {boolean} [showNextButton=false] - Whether to show the next button.
 * @prop {boolean} [showSubmitButton=true] - Whether to show the submit button.
 * @prop {string} [submitDataTest='modal-action-button'] - The data-test attribute for the submit button.
 * @prop {CAButtonStyle} [submitStyle='filled-dark'] - The style of the submit button.
 * @prop {string} [submitText='Submit'] - The text for the submit button.
 *
 * @emit back - Emits when the back button is clicked.
 * @emit cancel - Emits when the cancel button is clicked.
 * @emit next - Emits when the next button is clicked.
 * @emit submit - Emits when the submit button is clicked.
 */
export default defineComponent({
	name: 'CAButtonGroupFooter',

	props: {
		/**
		 * The data-test attribute for the back button.
		 * @default 'modal-back-button'
		 * @type {string}
		 */
		backDataTest: { default: 'modal-back-button', type: String },
		/**
		 * The text for the back button.
		 * @default 'Back'
		 * @type {string}
		 */
		backText: { default: 'back', type: String },
		/**
		 * The type of the button.
		 * @default 'primary'
		 * @type {CAButtonType}
		 * @enum {'primary' | 'danger' | 'group' | 'info' | 'primary-alt' | 'secondary' | 'slideover' | 'success' | 'warning'}
		 */
		buttonType: { default: 'primary', type: String as PropType<CAButtonType> },
		/**
		 * The direction of the buttons in the footer: 'row' | 'column'
		 *
		 * @default 'row'
		 * @type {String}
		 * @enum {'row' | 'column'}
		 */
		buttonsDirection: { default: 'row', type: String as PropType<'column' | 'row'> },
		/**
		 * The data-test attribute for the cancel button.
		 * @default 'modal-close-button'
		 * @type {string}
		 */
		cancelDataTest: { default: 'modal-close-button', type: String },
		/**
		 * The text for the cancel button.
		 * @default 'Cancel'
		 * @type {string}
		 */
		cancelText: { default: 'Cancel', type: String },
		/**
		 * Whether the buttons are disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabled: { default: false, type: Boolean },
		/**
		 * Whether the cancel button is disabled.
		 * @default false
		 * @type {boolean}
		 */
		disabledCancel: { default: false, type: Boolean },
		/**
		 * The data-test attribute for the next button.
		 * @default 'modal-next-button'
		 * @type {string}
		 */
		nextDataTest: { default: 'modal-next-button', type: String },
		/**
		 * The text for the next button.
		 * @default 'Next'
		 * @type {string}
		 */
		nextText: { default: 'Next', type: String },
		/**
		 * Whether to show the back button.
		 * @default false
		 * @type {boolean}
		 */
		showBackButton: { default: false, type: Boolean },
		/**
		 * Whether to show the cancel button.
		 * @default true
		 * @type {boolean}
		 */
		showCancelButton: { default: true, type: Boolean },
		/**
		 * Whether to show the next button.
		 * @default false
		 * @type {boolean}
		 */
		showNextButton: { default: false, type: Boolean },
		/**
		 * Whether to show the submit button.
		 * @default true
		 * @type {boolean}
		 */
		showSubmitButton: { default: true, type: Boolean },
		/**
		 * The data-test attribute for the submit button.
		 * @default 'modal-action-button'
		 * @type {string}
		 */
		submitDataTest: { default: 'modal-action-button', type: String },
		/**
		* The style of the submit button.
		 * @default 'filled-dark'
		 * @type {CAButtonStyle}
		 * @enum {('dark' | 'filled-dark' | 'filled-light' | 'outlined')}
		 */
		submitStyle: { default: 'filled-dark', type: String as PropType<CAButtonStyle> },
		/**
		 * The text for the submit button.
		 * @default 'Submit'
		 * @type {string}
		 */
		submitText: { default: 'Submit', type: String },
	},

	emits: [
		'back',
		'cancel',
		'next',
		'submit',
	],

	render() {
		const directionClass = this.buttonsDirection === 'column' ? 'flex-col' : 'flex-row'

		return (
			<div class={`ca-button-group-footer ${directionClass}`} data-test="ca-button-group-footer">
				{this.showBackButton &&
					<CAButton
						buttonStyle="outlined"
						data-test={this.backDataTest}
						text={this.backText}
						type="secondary"
						onClick={() => this.$emit('back')}
					/>
				}

				{this.showCancelButton &&
					<CAButton
						buttonStyle="outlined"
						data-test={this.cancelDataTest}
						disabled={this.disabledCancel}
						text={this.cancelText}
						type="primary"
						onClick={() => this.$emit('cancel')}
					/>
				}

				{this.showNextButton &&
					<CAButton
						data-test={this.nextDataTest}
						disabled={this.disabled}
						text={this.nextText}
						type={this.buttonType}
						onClick={() => this.$emit('next')}
					/>
				}

				{this.showSubmitButton &&
					<CAButton
						buttonStyle={this.submitStyle}
						data-test={this.submitDataTest}
						disabled={this.disabled}
						text={this.submitText}
						type={this.buttonType}
						onClick={() => this.$emit('submit')}
					/>
				}
			</div>
		)
	},
})
